import { z } from "zod";

import {
  Allergies,
  ConsultationReasons,
  DiabeticPatient,
  Diseases,
  Genders,
  GravityStates,
  Pathologies,
  SmokingPatient,
  ToothBrushHairTypes,
  ToothBrushTypes,
  WomenHealth,
} from "@/types/api/patient";

export const createPatientSchema = z.object({
  lastName: z.string({ message: "Renseignez le nom du patient" }),
  firstName: z.string({ message: "Renseignez le prénom du patient" }),
  email: z.string({ message: "Renseignez l'adresse mail du patient" }),
  gender: z.enum(["MALE", "FEMALE", "OTHER"], {
    message: "Renseignez le genre du patient",
  }),
  birthDate: z.coerce.date({
    message: "Renseignez la date de naissance du patient",
  }),
  consultationMotive: z.enum(
    [
      "BLEEDING",
      "MOBILITY",
      "PAIN",
      "HALITOSE",
      "AESTHETIC",
      "CONTROL",
      "ADRESSED",
    ],
    {
      message: "Renseignez le genre du patient",
    }
  ),
});

export type CreatePatientFormData = z.infer<typeof createPatientSchema>;

export const createPatientDefaultValues: CreatePatientFormData = {
  lastName: "",
  firstName: "",
  email: "",
  gender: "FEMALE",
  birthDate: new Date(),
  consultationMotive: "BLEEDING",
};

export const updatePatientSchema = z.object({
  firstName: z
    .string()
    .min(1, { message: "Le prénom ne doit pas être vide" })
    .nullish(),
  lastName: z
    .string()
    .min(1, { message: "Le nom ne doit pas être vide" })
    .nullish(),
  gender: z.enum(Genders).nullish(),
  mobilePhone: z.string().nullish(),
  email: z
    .string()
    .email({ message: "L'email doit être un email valide" })
    .nullish(),
  birthDate: z.coerce.date().nullish(),
  address: z.string().nullish(),
  postalCode: z.number().nullish(),
  city: z.string().nullish(),
  consultationMotive: z.enum(ConsultationReasons).nullish(),
  homePhone: z.string().nullish(),
  job: z.string().nullish(),
  addressComplement: z.string().nullish(),
  toothBrush: z.enum(ToothBrushTypes).nullish(),
  toothBrushHair: z.enum(ToothBrushHairTypes).nullish(),
  brushingFrequency: z.string().nullish(),
  interdentalSystem: z.boolean().nullish(),
  brushingAccessories: z.string().nullish(),
  toothPaste: z.string().nullish(),
  toothState: z.enum(GravityStates).nullish(),
  mouthWash: z.string().nullish(),
  smoking: z.enum(SmokingPatient).nullish(),
  dailySmokes: z.number().min(0).nullish(),
  smokingEnd: z.coerce.date().nullish(),
  smokingState: z.enum(GravityStates).nullish(),
  diabetic: z.enum(DiabeticPatient).nullish(),
  diabetComment: z.string().nullish(),
  diabetState: z.enum(GravityStates).nullish(),
  hba1c: z.number().min(0).max(100).nullish(),
  geneticTendency: z.boolean().nullish(),
  geneticComment: z.string().nullish(),
  geneticState: z.enum(GravityStates).nullish(),
  stress: z.number().min(1).max(10).nullish(),
  stressState: z.enum(GravityStates).nullish(),
  stressComment: z.string().nullish(),
  associatedPathologies: z.array(z.enum(Pathologies)).nullish(),
  pathologyComment: z.string().nullish(),
  allergies: z.array(z.enum(Allergies)).nullish(),
  allergiesComment: z.string().nullish(),
  womenHealth: z.array(z.enum(WomenHealth)).nullish(),
  pregnancyEnd: z.coerce.date().nullish(),
  womenHealthComment: z.string().nullish(),
  sportsComment: z.string().nullish(),
  dietComment: z.string().nullish(),
  disease: z.enum(Diseases).nullish(),
  diseaseState: z.enum(GravityStates).nullish(),
  patientComment: z.string().nullish(),
  bleedingIndex: z.number().min(0).max(100).nullish(),
  plaqueIndex: z.number().min(0).max(100).nullish(),
  stateGradeAbscess: z.string().nullish(),
  consultationMotiveComment: z.string().nullish(),
});

export type UpdatePatientFormData = z.infer<typeof updatePatientSchema>;

export const updatePatientDefaultValues: UpdatePatientFormData = {
  firstName: null,
  lastName: null,
  gender: null,
  mobilePhone: null,
  email: null,
  birthDate: null,
  address: null,
  postalCode: null,
  city: null,
  consultationMotive: null,
  homePhone: null,
  job: null,
  addressComplement: null,
  toothBrush: null,
  toothBrushHair: null,
  brushingFrequency: null,
  interdentalSystem: null,
  brushingAccessories: null,
  toothPaste: null,
  toothState: null,
  mouthWash: null,
  smoking: null,
  dailySmokes: null,
  smokingEnd: null,
  smokingState: null,
  diabetic: null,
  diabetComment: null,
  diabetState: null,
  hba1c: null,
  geneticTendency: null,
  geneticComment: null,
  geneticState: null,
  stress: null,
  stressState: null,
  stressComment: null,
  associatedPathologies: null,
  allergies: null,
  allergiesComment: null,
  womenHealth: null,
  pregnancyEnd: null,
  womenHealthComment: null,
  sportsComment: null,
  dietComment: null,
  bleedingIndex: null,
  plaqueIndex: null,
  stateGradeAbscess: null,
  consultationMotiveComment: null,
};

export const patchBrushingSchema = z.object({
  toothBrush: z.enum(ToothBrushTypes).nullable(),
  toothBrushHair: z.enum(ToothBrushHairTypes).nullable(),
  brushingFrequency: z.string().nullable(),
  interdentalSystem: z.boolean(),
  brushingAccessories: z.string().nullable(),
  toothPaste: z.string().nullable(),
  toothState: z.enum(GravityStates),
  mouthWash: z.string().nullable(),
});

export type PatchBrushingFormData = z.infer<typeof patchBrushingSchema>;

export const patchBrushingDefaultValues: PatchBrushingFormData = {
  toothBrush: null,
  toothBrushHair: null,
  brushingFrequency: null,
  brushingAccessories: null,
  interdentalSystem: false,
  toothPaste: null,
  toothState: "NONE",
  mouthWash: null,
};

export const patchSmokingSchema = z.object({
  smokingState: z.enum(GravityStates),
  smoking: z.enum(SmokingPatient),
  dailySmokes: z.number().min(0).nullable(),
  smokingEnd: z.coerce.date().nullable(),
});

export type PatchSmokingFormData = z.infer<typeof patchSmokingSchema>;

export const patchSmokingDefaultValues: PatchSmokingFormData = {
  smokingState: "NONE",
  smoking: "FALSE",
  dailySmokes: null,
  smokingEnd: null,
};

export const patchDiabeticSchema = z.object({
  diabetState: z.enum(GravityStates),
  diabetic: z.enum(DiabeticPatient),
  hba1c: z.number().nullable(),
  diabetComment: z.string().nullable(),
});

export type PatchDiabeticFormData = z.infer<typeof patchDiabeticSchema>;

export const patchDiabeticDefaultValues: PatchDiabeticFormData = {
  diabetState: "NONE",
  diabetic: "NONE",
  hba1c: null,
  diabetComment: null,
};

export const patchGeneticSchema = z.object({
  geneticState: z.enum(GravityStates),
  geneticTendency: z.boolean(),
  geneticComment: z.string().nullable(),
});

export type PatchGeneticFormData = z.infer<typeof patchGeneticSchema>;

export const patchGeneticDefaultValues: PatchGeneticFormData = {
  geneticState: "NONE",
  geneticTendency: false,
  geneticComment: null,
};

export const patchStressSchema = z.object({
  stressState: z.enum(GravityStates),
  stress: z.number().min(1).max(10),
  stressComment: z.string().nullable(),
});

export const patchPathologiesSchema = z.object({
  associatedPathologies: z.array(z.enum(Pathologies)).nullable(),
  pathologyComment: z.string().nullable(),
});

export type PatchPathologiesFormData = z.infer<typeof patchPathologiesSchema>;

export const patchAllergiesSchema = z.object({
  allergies: z.array(z.enum(Allergies)).nullable(),
  allergiesComment: z.string().nullable(),
});

export type PatchAllergiesFormData = z.infer<typeof patchAllergiesSchema>;

export const patchWomenHealthSchema = z.object({
  pregnancyEnd: z.coerce.date().nullable(),
  womenHealth: z.array(z.enum(WomenHealth)).nullable(),
  womenHealthComment: z.string().nullable(),
});

export type PatchWomenHealthFormData = z.infer<typeof patchWomenHealthSchema>;

export const patchSportsSchema = z.object({
  sportsComment: z.string().nullable(),
});

export const patchDietSchema = z.object({
  dietComment: z.string().nullable(),
});

export const patchAdministrativeInfosSchema = z.object({
  birthDate: z.coerce.date(),
  email: z.string().email({ message: "L'email doit être un email valide" }),
  homePhone: z.string().nullable(),
  mobilePhone: z.string().nullable(),
  job: z.string().nullable(),
  address: z.string().nullable(),
  addressComplement: z.string().nullable(),
  postalCode: z.number().nullable(),
  city: z.string().nullable(),
});

export const patchProfileStateSchema = z.object({
  consultationMotive: z.enum(ConsultationReasons),
  diseaseState: z.enum(GravityStates),
});

export const patchDiseaseSchema = z.object({
  disease: z.enum(Diseases),
});

export const patchPatientCommentSchema = z.object({
  patientComment: z.string().nullable(),
});

export const patchIndexesSchema = z.object({
  bleedingIndex: z
    .number()
    .min(0, { message: "La valeur doit être supérieure ou égale à 0" })
    .max(100, { message: "La valeur doit être inférieure ou égale à 100" })
    .nullable(),
  plaqueIndex: z
    .number()
    .min(0, { message: "La valeur doit être supérieure ou égale à 0" })
    .max(100, { message: "La valeur doit être inférieure ou égale à 100" })
    .nullable(),
});

export type PatchIndexesFormData = z.infer<typeof patchIndexesSchema>;

export const patchStateGradeAbscessSchema = z.object({
  stateGradeAbscess: z.string().nullable(),
});

export type PatchStateGradeAbscessFormData = z.infer<
  typeof patchStateGradeAbscessSchema
>;

export const patchPatientConsultationMotiveCommentSchema = z.object({
  consultationMotiveComment: z.string().nullable(),
});

export type PatchPatientConsultationMotiveCommentFormData = z.infer<
  typeof patchPatientConsultationMotiveCommentSchema
>;
